import { PHASE_CODE, normalizeObjectTypeForLookup } from '../constants/object-workflow/phase-codes';
import { isSamePhase } from '../constants/object-workflow/workflow-model';
import {
  canViewOtherUsersActivityAndWorkReport,
  hasAdminOrSuperAdminRole,
  hasFinanceAdminOrSuperAdminRole,
} from '../constants/roles.constants';
import { permissionAllows } from '../constants/user-permission-profile';
import {
  isWorkReportPaymentLockedForUser,
} from '../constants/work-report.constants';

/** Normalizace e-mailu vlastníka řádku výkazu (parita web/backend). */
export function normalizeWorkReportOwnerEmail(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

export function isWorkReportEntryOwnedByViewer(
  rowUserId: string | null | undefined,
  viewerEmail: string | null | undefined,
): boolean {
  const owner = normalizeWorkReportOwnerEmail(rowUserId);
  const viewer = normalizeWorkReportOwnerEmail(viewerEmail);
  return owner.length > 0 && viewer.length > 0 && owner === viewer;
}

/** Účty osmi výkopových firem. Stavbyvedoucí smí jejich neproplacený výkaz upravovat a mazat. */
export function isVykopCompanyWorkReportOwner(email: string | null | undefined): boolean {
  return normalizeWorkReportOwnerEmail(email).endsWith('@vykop.cz');
}

/**
 * Záznam výkazu za jiného uživatele (plus i tužka).
 * Admin/superadmin jako dřív. Jinak uložené `work-report.edit` a buď role, která smí číst cizí výkaz
 * (finance / admin), nebo `users.view` (stavbyvedoucí nad výkopovými firmami).
 * Samotný příznak edit mají i terénní účty — bez `users.view` jim cizí řádek zůstává zavřený.
 */
export function mayEditOthersWorkReport(
  roles: string[] | null | undefined,
  rawProfile: unknown,
): boolean {
  if (hasAdminOrSuperAdminRole(roles)) return true;
  if (!permissionAllows(rawProfile, 'work-report', 'edit')) return false;
  if (canViewOtherUsersActivityAndWorkReport(roles)) return true;
  return permissionAllows(rawProfile, 'users', 'view');
}

/**
 * Cizí řádek, který tento účet smí upravit.
 * Finance a admin libovolný. Jinak jen řádek účtu @vykop.cz
 * (`work-report.edit` a `users.view`). Role skladu ani `org_admin` samy o sobě řádek neotevřou.
 */
export function mayEditOtherWorkReportRow(
  roles: string[] | null | undefined,
  rawProfile: unknown,
  rowUserId: string | null | undefined,
): boolean {
  if (!mayEditOthersWorkReport(roles, rawProfile)) return false;
  if (hasAdminOrSuperAdminRole(roles) || hasFinanceAdminOrSuperAdminRole(roles)) return true;
  return isVykopCompanyWorkReportOwner(rowUserId);
}

/**
 * Uživatel smí upravit parametry (délka, šířka, hloubka, …) jen u vlastního řádku;
 * admin u všech. Vyfakturovaný řádek smí měnit jen admin.
 * `mayEditOthers` pustí neproplacený cizí řádek držiteli `work-report.edit` (ne vyfakturovaný).
 */
export function canEditWorkReportEntryParams(
  rowUserId: string | null | undefined,
  viewerEmail: string | null | undefined,
  isAdmin: boolean,
  paymentStatus?: string | null,
  mayEditOthers = false,
): boolean {
  if (isAdmin) return true;
  if (isWorkReportPaymentLockedForUser(paymentStatus, false)) {
    return false;
  }
  if (mayEditOthers) return true;
  return isWorkReportEntryOwnedByViewer(rowUserId, viewerEmail);
}

/**
 * Smazání řádku. Admin jako dřív. Jinak `work-report.delete` spolu s `users.view`
 * a jen u neproplaceného řádku účtu @vykop.cz.
 */
export function canDeleteWorkReportEntry(
  isAdmin: boolean,
  rawProfile?: unknown,
  rowUserId?: string | null,
  paymentStatus?: string | null,
): boolean {
  if (isAdmin) return true;
  if (rawProfile === undefined) return false;
  if (isWorkReportPaymentLockedForUser(paymentStatus, false)) return false;
  if (!permissionAllows(rawProfile, 'work-report', 'delete')) return false;
  if (!permissionAllows(rawProfile, 'users', 'view')) return false;
  return isVykopCompanyWorkReportOwner(rowUserId);
}

function isExcavationObjectType(objectType: string | null | undefined): boolean {
  const type = (objectType ?? '').toString().toUpperCase();
  const typeKey = normalizeObjectTypeForLookup(type);
  return typeKey === 'VYKOP' || type === 'JAMA';
}

function endsWithPhaseIndex(status: string | null | undefined, phaseIndex: number): boolean {
  const current = (status ?? '').trim();
  if (!current) return false;
  return current.endsWith(`.PHASE.${phaseIndex}`);
}

/** Modal Výkop — editace jen ve fázi Nevykopaný (PHASE.0). */
export function isVykopSheetEditablePhase(
  objectType: string | null | undefined,
  status: string | null | undefined,
): boolean {
  if (!isExcavationObjectType(objectType)) return false;
  return endsWithPhaseIndex(status, 0);
}

/** Modal Obnova — editace jen ve fázi Vykopaný (PHASE.1). */
export function isObnovaSheetEditablePhase(
  objectType: string | null | undefined,
  status: string | null | undefined,
): boolean {
  if (!isExcavationObjectType(objectType)) return false;
  return endsWithPhaseIndex(status, 1);
}

/** Tlačítko Výkop viditelné ve fázi Nevykopaný nebo Vykopaný (admin vždy). */
export function isVykopSheetVisible(
  objectType: string | null | undefined,
  status: string | null | undefined,
  isAdmin: boolean,
): boolean {
  if (!isExcavationObjectType(objectType)) return false;
  if (isAdmin) return true;
  return endsWithPhaseIndex(status, 0) || endsWithPhaseIndex(status, 1);
}

/** Tlačítko Obnova viditelné ve fázi Vykopaný nebo Finální (admin vždy). */
export function isObnovaSheetVisible(
  objectType: string | null | undefined,
  status: string | null | undefined,
  isAdmin: boolean,
): boolean {
  if (!isExcavationObjectType(objectType)) return false;
  if (isAdmin) return true;
  return endsWithPhaseIndex(status, 1) || endsWithPhaseIndex(status, 2);
}

export function isVykopSheetEditableOnMap(
  objectType: string | null | undefined,
  status: string | null | undefined,
  isAdmin: boolean,
): boolean {
  if (isAdmin) return true;
  return isVykopSheetEditablePhase(objectType, status);
}

export function isObnovaSheetEditableOnMap(
  objectType: string | null | undefined,
  status: string | null | undefined,
  isAdmin: boolean,
): boolean {
  if (isAdmin) return true;
  return isObnovaSheetEditablePhase(objectType, status);
}

/** Povrchy výkopu povinné pro přechod Nevykopaný → Vykopaný (PHASE.0 → PHASE.1). */
export function needsExcavationData(
  objectType: string | null | undefined,
  currentStatus: string | null | undefined,
  nextPhaseCode: string | null | undefined,
): boolean {
  if (!isExcavationObjectType(objectType)) return false;
  return endsWithPhaseIndex(currentStatus, 0) && endsWithPhaseIndex(nextPhaseCode, 1);
}

/**
 * Obnova ve 2. fázi (Vykopaný → Finální, PHASE.1 → PHASE.2) je volitelná:
 * - v režimu `linear` (m) se v Obnově zadává jen volitelná nad rámec obnova a hrabičkování,
 * - v režimu `volume` (m³ / m²) se obnova objemu i povrchu automaticky přebírá z Výkopu, pokud uživatel v Obnově nezmění povrch.
 */
export function needsObnovaData(
  _objectType: string | null | undefined,
  _currentStatus: string | null | undefined,
  _nextPhaseCode: string | null | undefined,
): boolean {
  return false;
}

/**
 * Celkový modal Povrchy (Obnova + náhled Výkopu) — editace Obnovy ve Vykopaném.
 * Samotné rozměry Výkopu jsou po PHASE.0 zamčené (`areVykopDimensionsLocked`).
 */
export function isExcavationSurfacesEditablePhase(
  objectType: string | null | undefined,
  status: string | null | undefined,
): boolean {
  const type = (objectType ?? '').toString().toUpperCase();
  const typeKey = normalizeObjectTypeForLookup(type);
  const current = (status ?? '').trim();
  if (!current) return false;

  if (typeKey === 'VYKOP') {
    return (
      isSamePhase(current, PHASE_CODE.VYKOP_NEVYKOPANY) ||
      isSamePhase(current, PHASE_CODE.VYKOP_VYKOPANY)
    );
  }
  if (type === 'JAMA') {
    return (
      isSamePhase(current, PHASE_CODE.JAMA_NEVYKOPANA) ||
      isSamePhase(current, PHASE_CODE.JAMA_VYKOPANA)
    );
  }
  return false;
}

/** Mapa: admin může povrchy měnit vždy; ostatní jen ve fázi Nevykopaný/Vykopaný. */
export function isExcavationSurfacesEditableOnMap(
  objectType: string | null | undefined,
  status: string | null | undefined,
  isAdmin: boolean,
): boolean {
  if (isAdmin) return true;
  return isExcavationSurfacesEditablePhase(objectType, status);
}

export interface WorkReportEntryUpdateBody {
  activityName?: string;
  description?: string;
  price?: number;
  excavationLengthM?: number;
  lengthM?: number;
  quantity?: number;
  widthM?: number;
  depthM?: number;
  deepeningCm?: number;
  propagateToObject?: boolean;
  ukonceniVRd?: string;
  ukonceniVRdOther?: string;
  /** ISO timestamp — pouze admin/superadmin (guard v sanitize). */
  createdAt?: string;
}

/** Backend guard: běžný uživatel smí poslat jen parametry; admin i metadata a cenu. */
export function sanitizeWorkReportEntryUpdateBody(
  body: WorkReportEntryUpdateBody,
  isAdmin: boolean,
): WorkReportEntryUpdateBody {
  if (isAdmin) return { ...body };
  const out: WorkReportEntryUpdateBody = {
    lengthM: body.lengthM ?? body.excavationLengthM,
    quantity: body.quantity,
    widthM: body.widthM,
    depthM: body.depthM,
    deepeningCm: body.deepeningCm,
    propagateToObject: body.propagateToObject,
    ukonceniVRd: body.ukonceniVRd,
    ukonceniVRdOther: body.ukonceniVRdOther,
  };
  return out;
}
